import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Crown, Download, ExternalLink, FileText, RefreshCw, Check, AlertCircle, Users, Sparkles, Clock, ArrowRight } from 'lucide-react';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useBankAccountStore } from '../store/bankAccountStore';
import * as subscriptionService from '../services/subscription';
import api from '../services/api';
import PricingTable from '../components/subscription/PricingTable';
import DowngradeModal from '../components/subscription/DowngradeModal';
import PlanBadge from '../components/common/PlanBadge';
import type { InvoiceDto, PlanType, BillingCycle } from '../types';

// Limites par plan (doit correspondre au backend config/stripe.ts)
const PLAN_LIMITS: Record<PlanType, { personalAccounts: number; sharedAccounts: number; budgets: number; envelopes: number }> = {
  free: { personalAccounts: 1, sharedAccounts: 1, budgets: 2, envelopes: 1 },
  premium: { personalAccounts: Infinity, sharedAccounts: 1, budgets: Infinity, envelopes: Infinity },
  duo: { personalAccounts: Infinity, sharedAccounts: Infinity, budgets: Infinity, envelopes: Infinity },
};

export default function Subscription() {
  const { t, i18n } = useTranslation('subscription');
  const { t: tc } = useTranslation('common');
  const dateLocale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
  const [searchParams, setSearchParams] = useSearchParams();

  const { planInfo, fetchPlanInfo } = useSubscriptionStore();
  const { bankAccounts, sharedBankAccounts, fetchBankAccounts, fetchSharedBankAccounts } = useBankAccountStore();

  const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [downgradeTarget, setDowngradeTarget] = useState<{ plan: PlanType; billingCycle: BillingCycle } | null>(null);
  const [isCancellingPending, setIsCancellingPending] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [budgets, setBudgets] = useState<{ id: string; name: string; bankAccountIds: string[] | null; type: 'personal' | 'shared' }[]>([]);
  const [envelopes, setEnvelopes] = useState<{ id: string; name: string; type: 'personal' | 'shared' }[]>([]);

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      showNotification('success', tc('success'));
      fetchPlanInfo();
      setSearchParams({}, { replace: true });
    } else if (searchParams.get('cancelled') === 'true') {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchPlanInfo, showNotification, tc]);

  // Charger les comptes et budgets pour le modal de downgrade
  useEffect(() => {
    fetchBankAccounts();
    fetchSharedBankAccounts();
    const loadBudgets = async () => {
      try {
        const response = await api.get('/budgets?listOnly=true');
        if (response.data.success && response.data.data) {
          setBudgets(response.data.data);
        }
      } catch {
        // Silently fail
      }
    };
    loadBudgets();
    const loadEnvelopes = async () => {
      try {
        const [personalRes, sharedRes] = await Promise.all([
          api.get('/envelopes?type=personal&status=active,spending'),
          api.get('/envelopes?type=shared&status=active,spending').catch(() => ({ data: { success: true, data: [] } })),
        ]);
        const all: { id: string; name: string; type: 'personal' | 'shared' }[] = [];
        if (personalRes.data.success) {
          all.push(...personalRes.data.data.map((e: { id: string; name: string }) => ({ id: e.id, name: e.name, type: 'personal' as const })));
        }
        if (sharedRes.data.success) {
          all.push(...sharedRes.data.data.map((e: { id: string; name: string }) => ({ id: e.id, name: e.name, type: 'shared' as const })));
        }
        setEnvelopes(all);
      } catch {
        // Silently fail
      }
    };
    loadEnvelopes();
  }, [fetchBankAccounts, fetchSharedBankAccounts]);

  useEffect(() => {
    const loadInvoices = async () => {
      setIsLoadingInvoices(true);
      try {
        const response = await subscriptionService.getInvoices();
        if (response.success && response.data) {
          setInvoices(response.data);
        }
      } catch {
        // Silently fail
      } finally {
        setIsLoadingInvoices(false);
      }
    };
    loadInvoices();
  }, []);

  // Callback après un changement de plan via PricingTable
  const handlePlanChanged = (type: 'immediate' | 'scheduled', effectiveDate?: string) => {
    if (type === 'immediate') {
      showNotification('success', t('planChange.upgradeSuccess'));
    } else if (effectiveDate) {
      const dateStr = new Date(effectiveDate).toLocaleDateString(dateLocale);
      showNotification('success', t('planChange.scheduledSuccess', { date: dateStr }));
    }
    fetchPlanInfo();
    setShowPlans(false);
  };

  // Annuler un changement de plan programmé
  const handleCancelPendingChange = async () => {
    setIsCancellingPending(true);
    try {
      const response = await subscriptionService.cancelPendingChange();
      if (response.success) {
        showNotification('success', t('planChange.pendingCancelled'));
        fetchPlanInfo();
      }
    } catch {
      showNotification('error', tc('errorOccurred'));
    } finally {
      setIsCancellingPending(false);
    }
  };

  // Vérifier si une sélection de downgrade est nécessaire avant d'annuler
  const handleCancelClick = () => {
    const currentLimits = PLAN_LIMITS[effectivePlan];
    const targetLimits = PLAN_LIMITS.free;
    // Ne demander la sélection que si le plan cible est plus restrictif que le plan actuel
    const needsPersonalSelection = bankAccounts.length > targetLimits.personalAccounts && targetLimits.personalAccounts < currentLimits.personalAccounts;
    const needsSharedSelection = sharedBankAccounts.length > targetLimits.sharedAccounts && targetLimits.sharedAccounts < currentLimits.sharedAccounts;
    const needsBudgetSelection = budgets.length > targetLimits.budgets && targetLimits.budgets < currentLimits.budgets;

    setDowngradeTarget({ plan: 'free', billingCycle: 'monthly' });

    if (needsPersonalSelection || needsSharedSelection || needsBudgetSelection) {
      setShowDowngradeModal(true);
    } else {
      // Pas de sélection nécessaire, annuler directement
      performCancel();
    }
  };

  // Gérer un downgrade depuis le PricingTable (ex: Duo → Premium)
  const handlePlanDowngrade = (targetPlan: PlanType, billingCycle: BillingCycle) => {
    const currentLimits = PLAN_LIMITS[effectivePlan];
    const targetLimits = PLAN_LIMITS[targetPlan];
    // Ne demander la sélection que si le plan cible est plus restrictif que le plan actuel
    const needsPersonalSelection = bankAccounts.length > targetLimits.personalAccounts && targetLimits.personalAccounts < currentLimits.personalAccounts;
    const needsSharedSelection = sharedBankAccounts.length > targetLimits.sharedAccounts && targetLimits.sharedAccounts < currentLimits.sharedAccounts;
    const needsBudgetSelection = budgets.length > targetLimits.budgets && targetLimits.budgets < currentLimits.budgets;

    setDowngradeTarget({ plan: targetPlan, billingCycle });

    if (targetPlan === 'free') {
      // Annulation vers gratuit
      if (needsPersonalSelection || needsSharedSelection || needsBudgetSelection) {
        setShowDowngradeModal(true);
      } else {
        performCancel();
      }
    } else {
      // Changement de plan payant (ex: Duo → Premium)
      if (needsPersonalSelection || needsSharedSelection || needsBudgetSelection) {
        setShowDowngradeModal(true);
      } else {
        performPlanChange(targetPlan, billingCycle);
      }
    }
  };

  // Exécuter un changement de plan payant (après sélection éventuelle)
  const performPlanChange = async (targetPlan: PlanType, billingCycle: BillingCycle, selection?: {
    keepPersonalAccountIds: string[];
    keepSharedAccountIds: string[];
    keepBudgetIds: string[];
    keepEnvelopeIds: string[];
  }) => {
    setIsCancelling(true);
    try {
      // Traiter la sélection de downgrade (archiver les ressources non retenues)
      if (selection) {
        if (selection.keepPersonalAccountIds.length === 1) {
          const accountId = selection.keepPersonalAccountIds[0];
          const currentDefault = bankAccounts.find(a => a.isDefault);
          if (!currentDefault || currentDefault.id !== accountId) {
            await api.put(`/bank-accounts/${accountId}/set-default`);
          }
        }
        if (selection.keepSharedAccountIds.length === 1) {
          const accountId = selection.keepSharedAccountIds[0];
          const currentDefault = sharedBankAccounts.find(a => a.isDefault);
          if (!currentDefault || currentDefault.id !== accountId) {
            await api.put(`/shared-bank-accounts/${accountId}/set-default`);
          }
        }
        await subscriptionService.processDowngradeSelection({
          keepBankAccountIds: selection.keepPersonalAccountIds,
          keepBudgetIds: selection.keepBudgetIds,
          keepEnvelopeIds: selection.keepEnvelopeIds,
        });
      }

      // Changer de plan via l'API
      const response = await subscriptionService.changePlan(
        targetPlan,
        billingCycle,
      );
      if (response.success && response.data) {
        handlePlanChanged(response.data.type, response.data.effectiveDate);
      }
    } catch {
      showNotification('error', tc('errorOccurred'));
    } finally {
      setIsCancelling(false);
      setShowDowngradeModal(false);
    }
  };

  // Exécuter l'annulation (après sélection éventuelle)
  const performCancel = async (selection?: {
    keepPersonalAccountIds: string[];
    keepSharedAccountIds: string[];
    keepBudgetIds: string[];
    keepEnvelopeIds: string[];
  }) => {
    setIsCancelling(true);
    try {
      // Traiter la sélection de downgrade (archiver les ressources non retenues)
      if (selection) {
        if (selection.keepPersonalAccountIds.length === 1) {
          const accountId = selection.keepPersonalAccountIds[0];
          const currentDefault = bankAccounts.find(a => a.isDefault);
          if (!currentDefault || currentDefault.id !== accountId) {
            await api.put(`/bank-accounts/${accountId}/set-default`);
          }
        }
        if (selection.keepSharedAccountIds.length === 1) {
          const accountId = selection.keepSharedAccountIds[0];
          const currentDefault = sharedBankAccounts.find(a => a.isDefault);
          if (!currentDefault || currentDefault.id !== accountId) {
            await api.put(`/shared-bank-accounts/${accountId}/set-default`);
          }
        }
        await subscriptionService.processDowngradeSelection({
          keepBankAccountIds: selection.keepPersonalAccountIds,
          keepBudgetIds: selection.keepBudgetIds,
          keepEnvelopeIds: selection.keepEnvelopeIds,
        });
      }

      const response = await subscriptionService.cancelSubscription();
      if (response.success) {
        showNotification('success', t('cancel.cancelled'));
        // Rafraîchir les données
        await fetchPlanInfo();
        fetchBankAccounts();
        fetchSharedBankAccounts();
      }
    } catch {
      showNotification('error', tc('errorOccurred'));
    } finally {
      setIsCancelling(false);
      setShowDowngradeModal(false);
    }
  };

  const handleDowngradeConfirm = (selection: {
    keepPersonalAccountIds: string[];
    keepSharedAccountIds: string[];
    keepBudgetIds: string[];
    keepEnvelopeIds: string[];
  }) => {
    if (downgradeTarget && downgradeTarget.plan !== 'free') {
      performPlanChange(downgradeTarget.plan, downgradeTarget.billingCycle, selection);
    } else {
      performCancel(selection);
    }
  };

  const handleResume = async () => {
    setIsResuming(true);
    try {
      const response = await subscriptionService.resumeSubscription();
      if (response.success) {
        showNotification('success', t('resume.success'));
        fetchPlanInfo();
      }
    } catch {
      showNotification('error', tc('errorOccurred'));
    } finally {
      setIsResuming(false);
    }
  };

  const handlePortalSession = async () => {
    try {
      const response = await subscriptionService.createPortalSession();
      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch {
      showNotification('error', tc('errorOccurred'));
    }
  };

  const subscription = planInfo?.subscription;
  const effectivePlan = planInfo?.effectivePlan || 'free';
  const isPaid = effectivePlan !== 'free';
  const isGranted = planInfo?.source === 'grant';
  const isDuoBeneficiary = planInfo?.source === 'duo_benefit';
  const isTrialing = subscription?.status === 'trialing';
  const isExpired = subscription?.status === 'expired';
  const hasSubscription = !!subscription && !isGranted && !isDuoBeneficiary && !isExpired;

  const trialDaysLeft = isTrialing && subscription?.trialEndDate
    ? Math.max(0, Math.ceil((new Date(subscription.trialEndDate).getTime() - Date.now()) / 86400000))
    : 0;

  const planIcons: Record<PlanType, typeof Crown> = { free: Sparkles, premium: Crown, duo: Users };
  const PlanIcon = planIcons[effectivePlan];

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Titre */}
      <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>

      {/* Notification */}
      {notification && (
        <div className={`flex items-center gap-3 p-4 rounded-xl text-sm ${
          notification.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {notification.type === 'success' ? <Check className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          {notification.message}
        </div>
      )}

      {/* ===== Carte plan actuel ===== */}
      <div className="card">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              effectivePlan === 'free' ? 'bg-gray-100' :
              effectivePlan === 'premium' ? 'bg-primary-50' : 'bg-purple-50'
            }`}>
              <PlanIcon className={`w-5 h-5 ${
                effectivePlan === 'free' ? 'text-gray-500' :
                effectivePlan === 'premium' ? 'text-primary-600' : 'text-purple-600'
              }`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('currentPlan')}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <PlanBadge plan={effectivePlan} size="md" granted={isGranted} />
                {hasSubscription && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    isTrialing
                      ? 'bg-blue-50 text-blue-600'
                      : subscription.status === 'active'
                        ? 'bg-green-50 text-green-600'
                        : subscription.status === 'past_due'
                          ? 'bg-red-50 text-red-600'
                          : 'bg-gray-100 text-gray-500'
                  }`}>
                    {t(`status.${subscription.status}`)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Info Duo bénéficiaire */}
        {isDuoBeneficiary && planInfo?.duoBenefactorName && (
          <div className="mt-4 p-3 rounded-lg bg-purple-50 border border-purple-100">
            <p className="text-sm text-purple-700">{t('duo.benefit', { name: planInfo.duoBenefactorName })}</p>
          </div>
        )}

        {/* Info grant */}
        {isGranted && (
          <div className="mt-4 p-3 rounded-lg bg-primary-50 border border-primary-100">
            <p className="text-sm text-primary-700">{t('grant.info')}</p>
          </div>
        )}

        {/* Détails abonnement */}
        {hasSubscription && (
          <div className="mt-4 space-y-4">
            {/* Barre de trial */}
            {isTrialing && subscription?.trialEndDate && (
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">
                    {t('trial.daysRemaining', { count: trialDaysLeft })}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.max(5, ((14 - trialDaysLeft) / 14) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  {t('trial.endsOn', { date: new Date(subscription.trialEndDate).toLocaleDateString(dateLocale) })}
                </p>
              </div>
            )}

            {/* Infos de période */}
            {!isTrialing && subscription.currentPeriodEnd && !subscription.cancelAtPeriodEnd && subscription.status === 'active' && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <CreditCard className="w-4 h-4" />
                {t('nextBilling', { date: new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale) })}
              </div>
            )}

            {/* Changement de plan programmé */}
            {subscription.pendingPlan && subscription.currentPeriodEnd && (
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <p className="text-sm text-blue-700">
                      {subscription.pendingPlan !== subscription.plan
                        ? t('planChange.pendingInfo', {
                            plan: t(`plans.${subscription.pendingPlan}`),
                            date: new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale),
                          })
                        : t('planChange.pendingCycleInfo', {
                            cycle: t(`planChange.cycleName_${subscription.pendingBillingCycle}`),
                            date: new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale),
                          })
                      }
                    </p>
                  </div>
                  <button
                    onClick={handleCancelPendingChange}
                    disabled={isCancellingPending}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap ml-3"
                  >
                    {isCancellingPending ? <RefreshCw className="w-3 h-3 animate-spin" /> : t('planChange.cancelPending')}
                  </button>
                </div>
              </div>
            )}

            {/* Annulation programmée */}
            {subscription.cancelAtPeriodEnd && subscription.currentPeriodEnd && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                <p className="text-sm text-amber-700">
                  {t('cancel.cancelledInfo', { date: new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale) })}
                </p>
              </div>
            )}

            {/* Abo en pause */}
            {subscription.status === 'paused' && (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                <p className="text-sm text-gray-600">{t('duo.pausedInfo')}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              {(subscription.status === 'active' || isTrialing) && !subscription.cancelAtPeriodEnd && (
                <>
                  <button onClick={handlePortalSession} className="btn-secondary btn-sm">
                    <CreditCard className="w-4 h-4 mr-1.5" />
                    {t('actions.managePayment')}
                  </button>
                  <button
                    onClick={() => setShowPlans(!showPlans)}
                    className="btn-outline btn-sm"
                  >
                    <ArrowRight className="w-4 h-4 mr-1.5" />
                    {t('actions.changePlan')}
                  </button>
                  <button
                    onClick={handleCancelClick}
                    disabled={isCancelling}
                    className="btn btn-sm text-red-600 hover:bg-red-50"
                  >
                    {isCancelling ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                    {isTrialing ? t('actions.cancelTrial') : t('actions.cancel')}
                  </button>
                </>
              )}

              {subscription.cancelAtPeriodEnd && (
                <button onClick={handleResume} disabled={isResuming} className="btn-primary btn-sm">
                  {isResuming && <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />}
                  {t('actions.resume')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Plan gratuit — CTA upgrade */}
        {effectivePlan === 'free' && !isGranted && !isDuoBeneficiary && !hasSubscription && (
          <div className="mt-4 p-4 rounded-xl bg-primary-50 border border-primary-100">
            <p className="text-sm text-primary-700 mb-3">{t('trial.description')}</p>
            <button onClick={() => setShowPlans(true)} className="btn-primary btn-sm">
              {t('actions.startTrial')}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </button>
          </div>
        )}
      </div>

      {/* ===== Tableau des plans ===== */}
      {(showPlans || (effectivePlan === 'free' && !isGranted && !isDuoBeneficiary && !hasSubscription)) && (
        <div className="card">
          <PricingTable
            onError={(msg) => showNotification('error', msg)}
            onDowngrade={handlePlanDowngrade}
            onPlanChanged={handlePlanChanged}
          />
        </div>
      )}

      {/* ===== Factures ===== */}
      {isPaid && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-gray-400" />
            {t('invoices.title')}
          </h2>

          {isLoadingInvoices ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
              <RefreshCw className="w-4 h-4 animate-spin" />
              {tc('loading')}
            </div>
          ) : invoices.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">{t('invoices.noInvoices')}</p>
          ) : (
            <div className="overflow-x-auto -mx-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-6 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invoices.date')}</th>
                    <th className="text-left px-6 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invoices.amount')}</th>
                    <th className="text-left px-6 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">{t('invoices.period')}</th>
                    <th className="text-left px-6 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invoices.status')}</th>
                    <th className="text-right px-6 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 text-gray-900">
                        {new Date(invoice.createdAt).toLocaleDateString(dateLocale)}
                      </td>
                      <td className="px-6 py-3 font-medium text-gray-900">
                        {(invoice.amountPaid / 100).toFixed(2)} {invoice.currency.toUpperCase()}
                      </td>
                      <td className="px-6 py-3 text-gray-500 hidden sm:table-cell">
                        {new Date(invoice.periodStart).toLocaleDateString(dateLocale)} — {new Date(invoice.periodEnd).toLocaleDateString(dateLocale)}
                      </td>
                      <td className="px-6 py-3">
                        <span className="badge-success">{t('invoices.paid')}</span>
                      </td>
                      <td className="px-6 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {invoice.invoicePdfUrl && (
                            <a
                              href={invoice.invoicePdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                              title={t('invoices.download')}
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                          {invoice.hostedInvoiceUrl && (
                            <a
                              href={invoice.hostedInvoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                              title={t('invoices.view')}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal de sélection de comptes avant downgrade */}
      <DowngradeModal
        isOpen={showDowngradeModal}
        onClose={() => { setShowDowngradeModal(false); setDowngradeTarget(null); }}
        onConfirm={handleDowngradeConfirm}
        personalAccounts={bankAccounts.map(a => ({ id: a.id, name: a.name, color: a.color, isDefault: a.isDefault }))}
        sharedAccounts={sharedBankAccounts.map(a => ({ id: a.id, name: a.name, color: a.color, isDefault: a.isDefault }))}
        budgets={budgets}
        envelopes={envelopes}
        maxPersonalAccounts={PLAN_LIMITS[downgradeTarget?.plan || 'free'].personalAccounts}
        maxSharedAccounts={PLAN_LIMITS[downgradeTarget?.plan || 'free'].sharedAccounts}
        maxBudgets={PLAN_LIMITS[downgradeTarget?.plan || 'free'].budgets}
        maxEnvelopes={PLAN_LIMITS[downgradeTarget?.plan || 'free'].envelopes}
        currentMaxPersonalAccounts={PLAN_LIMITS[effectivePlan].personalAccounts}
        currentMaxSharedAccounts={PLAN_LIMITS[effectivePlan].sharedAccounts}
        currentMaxBudgets={PLAN_LIMITS[effectivePlan].budgets}
        currentMaxEnvelopes={PLAN_LIMITS[effectivePlan].envelopes}
        isLoading={isCancelling}
        targetPlan={downgradeTarget?.plan || 'free'}
      />
    </div>
  );
}
